/**
 * Theme Context & Provider — "Happy Yellow" light-only theme.
 *
 * The former dark/purple mode has been removed. This provider now forces
 * a single bright, warm palette at boot and keeps the existing hook
 * signature for backwards compatibility with older components.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

// Legacy accent palette is frozen to warm-gold variants so any code that
// reads `ACCENT_COLORS[<key>]` still works without re-introducing purple.
export const ACCENT_COLORS = {
  gold: {
    name: 'Happy Gold',
    primary: '#FFC107',
    secondary: '#FFD700',
    gradient: 'linear-gradient(135deg, #FFD700, #FFC107)',
    hover: 'rgba(255, 215, 0, 0.15)',
    border: 'rgba(255, 193, 7, 0.4)',
  },
  amber: {
    name: 'Warm Amber',
    primary: '#F59E0B',
    secondary: '#FBBF24',
    gradient: 'linear-gradient(135deg, #FBBF24, #F59E0B)',
    hover: 'rgba(245, 158, 11, 0.15)',
    border: 'rgba(245, 158, 11, 0.4)',
  },
  sunflower: {
    name: 'Sunflower',
    primary: '#EAB308',
    secondary: '#FDE047',
    gradient: 'linear-gradient(135deg, #FDE047, #EAB308)',
    hover: 'rgba(234, 179, 8, 0.15)',
    border: 'rgba(234, 179, 8, 0.4)',
  },
  // Kept for backward compat — redirects to gold so old keys still work
  purple: null,
  pink: null,
  blue: null,
  green: null,
  orange: null,
  red: null,
};
// Backfill deprecated keys so ACCENT_COLORS[old_key] returns gold
['purple', 'pink', 'blue', 'green', 'orange', 'red'].forEach((k) => {
  ACCENT_COLORS[k] = ACCENT_COLORS.gold;
});

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};

export const ThemeProvider = ({ children }) => {
  // Dark mode removed. isDarkMode is always false and toggles are no-ops.
  const [accentColor, setAccentColor] = useState('gold');

  // Apply the light Happy Yellow palette once on mount (and when accent changes)
  useEffect(() => {
    const root = document.documentElement;
    const accent = ACCENT_COLORS[accentColor] || ACCENT_COLORS.gold;

    // Accent tokens
    root.style.setProperty('--accent-primary', accent.primary);
    root.style.setProperty('--accent-secondary', accent.secondary);
    root.style.setProperty('--accent-gradient', accent.gradient);
    root.style.setProperty('--accent-hover', accent.hover);
    root.style.setProperty('--accent-border', accent.border);

    // Legacy tokens remapped to gold
    root.style.setProperty('--primary-purple', accent.primary);
    root.style.setProperty('--blazing-pink', accent.secondary);

    // Always light mode
    root.classList.remove('dark-mode', 'dark');
    root.classList.add('light-mode');
    root.style.setProperty('--bg-primary', '#FDFBF7');
    root.style.setProperty('--bg-secondary', '#FFFFFF');
    root.style.setProperty('--bg-card', 'rgba(255,255,255,0.98)');
    root.style.setProperty('--bg-hover', accent.hover);
    root.style.setProperty('--text-primary', '#0F172A');
    root.style.setProperty('--text-secondary', '#334155');
    root.style.setProperty('--text-muted', '#64748B');
    root.style.setProperty('--border-color', accent.border);

    // Clear legacy dark-mode persistence
    try { localStorage.setItem('darkMode', 'false'); } catch (e) { /* ignore */ }
    try { localStorage.setItem('accentColor', accentColor); } catch (e) { /* ignore */ }
  }, [accentColor]);

  const noop = () => {};
  const changeAccentColor = (color) => {
    if (ACCENT_COLORS[color]) setAccentColor(color);
  };

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode: false,
        toggleDarkMode: noop,
        setIsDarkMode: noop,
        accentColor,
        changeAccentColor,
        accentColors: ACCENT_COLORS,
        currentAccent: ACCENT_COLORS[accentColor] || ACCENT_COLORS.gold,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// Theme Toggle component — now a small accent-color picker only.
// Dark-mode toggle removed to keep the UI consistently bright.
export const ThemeToggle = ({ compact = false, showColorPicker = true, onOpenGallery, onOpenPreview }) => {
  const { accentColor, changeAccentColor, accentColors, currentAccent } = useTheme();
  const [showColors, setShowColors] = useState(false);

  if (compact) {
    return (
      <button
        onClick={() => setShowColors((v) => !v)}
        style={{
          background: 'transparent',
          border: 'none',
          fontSize: '1.1rem',
          cursor: 'pointer',
          padding: 8,
          borderRadius: '50%',
        }}
        title="Change accent color"
        data-testid="theme-toggle-compact"
      >
        🌻
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {showColorPicker && (
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowColors(!showColors)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 16px',
              background: currentAccent.hover,
              border: `1px solid ${currentAccent.border}`,
              borderRadius: 10, cursor: 'pointer',
              transition: 'all 0.3s',
              color: '#92400E',
              fontSize: '0.85rem', fontWeight: 600, width: '100%',
            }}
            data-testid="accent-color-toggle"
          >
            <span style={{ width: 18, height: 18, borderRadius: '50%', background: currentAccent.gradient, border: '2px solid white' }} />
            <span>{currentAccent.name}</span>
            <span style={{ marginLeft: 'auto' }}>{showColors ? '▲' : '▼'}</span>
          </button>

          {showColors && (
            <div style={{
              position: 'absolute', bottom: '100%', left: 0, right: 0,
              background: 'rgba(255,255,255,0.98)',
              border: `1px solid ${currentAccent.border}`,
              borderRadius: 10, padding: 10, marginBottom: 5,
              boxShadow: '0 -4px 20px rgba(255,193,7,0.15)',
              zIndex: 1000,
            }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 8, fontWeight: 600 }}>
                🌻 Accent color
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                {['gold', 'amber', 'sunflower'].map((key) => {
                  const color = accentColors[key];
                  return (
                    <button
                      key={key}
                      onClick={() => { changeAccentColor(key); setShowColors(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px',
                        background: accentColor === key ? color.hover : 'transparent',
                        border: accentColor === key ? `2px solid ${color.primary}` : '1px solid transparent',
                        borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s',
                      }}
                      data-testid={`accent-color-${key}`}
                    >
                      <span style={{ width: 16, height: 16, borderRadius: '50%', background: color.gradient, flexShrink: 0 }} />
                      <span style={{ fontSize: '0.7rem', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {color.name.split(' ')[0]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {(onOpenGallery || onOpenPreview) && (
        <div style={{ display: 'flex', gap: 8 }}>
          {onOpenGallery && (
            <button
              onClick={onOpenGallery}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: '8px 12px',
                background: currentAccent.hover,
                border: `1px solid ${currentAccent.border}`,
                borderRadius: 8, cursor: 'pointer',
                color: '#92400E', fontSize: '0.75rem', fontWeight: 600,
              }}
              data-testid="open-theme-gallery"
            >
              🎨 Gallery
            </button>
          )}
          {onOpenPreview && (
            <button
              onClick={onOpenPreview}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: '8px 12px',
                background: currentAccent.hover,
                border: `1px solid ${currentAccent.border}`,
                borderRadius: 8, cursor: 'pointer',
                color: '#92400E', fontSize: '0.75rem', fontWeight: 600,
              }}
              data-testid="open-theme-preview"
            >
              👁️ Preview
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Backwards-compat re-exports
export const DarkModeProvider = ThemeProvider;
export const useDarkMode = useTheme;
export const DarkModeToggle = ThemeToggle;

export default ThemeContext;
